import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Icon, Textarea } from "@elit-hair/elit-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbCheck, TbMessageCircle, TbX } from "react-icons/tb";

import { useCreateFeedback } from "@/hooks/queries/feedback.queries";
import { ChatMessageRole } from "@/types/chatbot.types";
import { FeedbackMessageType } from "@/types/feedback.types";
import { convertMarkdownToSlack } from "@/utils/slack-formatter";

type ErrorReportModalProps = {
    isOpen: boolean;
    onClose: () => void;
    conversation?: Array<{
        role: ChatMessageRole;
        content: string;
    }>;
    reportedMessage?: {
        role: ChatMessageRole;
        content: string;
    };
};

export function ErrorReportModal({ isOpen, onClose, conversation = [], reportedMessage }: ErrorReportModalProps) {
    const { t } = useTranslation();
    const [description, setDescription] = useState("");
    const createFeedbackMutation = useCreateFeedback();

    const handleSubmit = async () => {
        if (!description.trim()) {
            return;
        }

        const cleanedConversation = conversation.map((msg) => ({
            ...msg,
            content: msg.role === ChatMessageRole.ASSISTANT ? convertMarkdownToSlack(msg.content) : msg.content,
        }));

        // Combine reported message with user comment
        const createCombinedMessage = (reportedMsg: typeof reportedMessage, userComment: string) => {
            if (!reportedMsg) return userComment;

            const separator = "━".repeat(40);
            const sections = [
                `⚠️ *Reported Message:*`,
                reportedMsg.content,
                "",
                separator,
                "",
                `💬 *User Comment:*`,
                userComment,
            ];

            return sections.join("\n");
        };

        const combinedMessage = createCombinedMessage(reportedMessage, description);

        // Apply markdown formatting to the combined message
        const formattedMessage = convertMarkdownToSlack(combinedMessage);

        createFeedbackMutation.mutate(
            {
                message: formattedMessage,
                messageType: FeedbackMessageType.CHATBOT_FEEDBACK,
                conversation: cleanedConversation,
            },
            {
                onSuccess: () => {
                    setDescription("");
                    onClose();
                },
            }
        );
    };

    const handleClose = () => {
        if (!createFeedbackMutation.isPending) {
            setDescription("");
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md z-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TbAlertTriangle className="w-5 h-5 text-orange-500" />
                        {t("error_report_title")}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">{t("error_report_description")}</p>

                    {/* Fixed preview area - always visible when there's a reported message */}
                    {reportedMessage && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t("preview")}
                            </label>
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-48 overflow-y-auto">
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <TbMessageCircle className="w-4 h-4 text-red-500" />
                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {t("reported_message")}
                                            </h4>
                                        </div>
                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                                            <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                                                {reportedMessage.content}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label htmlFor="error-description" className="text-sm font-medium">
                            {t("error_description")}
                        </label>
                        <Textarea
                            id="error-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t("error_description_placeholder")}
                            className="min-h-[100px] resize-none"
                            disabled={createFeedbackMutation.isPending}
                        />
                        {reportedMessage && (
                            <p className="text-xs text-muted-foreground">{t("will_send_with_message")}</p>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleClose} disabled={createFeedbackMutation.isPending}>
                            <Icon icon={TbX} className="w-4 h-4 mr-2" />
                            {t("cancel")}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={createFeedbackMutation.isPending || !description.trim()}
                            variant="default"
                        >
                            {createFeedbackMutation.isPending ? (
                                <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            ) : (
                                <TbCheck className="w-4 h-4 mr-2" />
                            )}
                            {t("submit_error_report")}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
